import { useState } from "react";
import { toast } from "sonner";
import { useContentDoc, LangTabs, setDeep, EditorToolbar } from "./ContentEditor";

const PAGES = ["home", "about", "services", "contact"];
const SERVICE_PAGES = ["business-consultancy", "property-development", "transaction-support"];

const SeoCard = ({ id, path, node, setContent }) => (
  <div className="bg-white border border-[#E3DED4] border-t-4 border-t-[#B85D2A] p-5" data-testid={`seo-card-${id}`}>
    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#8C6B4F] mb-3">/{id === "home" ? "" : id}</p>
    <label className="block text-xs font-medium text-[#526071] mb-1">Meta Title <span className="text-[#8A94A2]">({node.title.length} chars — aim 50–60)</span></label>
    <input
      data-testid={`seo-title-${id}`}
      className="w-full border border-[#E3DED4] px-3 py-2 text-sm mb-3 focus:outline-none focus:border-[#B85D2A]"
      value={node.title}
      onChange={(e) => setContent((c) => setDeep(c, [...path, "title"], e.target.value))}
    />
    <label className="block text-xs font-medium text-[#526071] mb-1">Meta Description <span className="text-[#8A94A2]">({node.desc.length} chars — aim 150–160)</span></label>
    <textarea
      data-testid={`seo-desc-${id}`}
      rows={3}
      className="w-full border border-[#E3DED4] px-3 py-2 text-sm focus:outline-none focus:border-[#B85D2A]"
      value={node.desc}
      onChange={(e) => setContent((c) => setDeep(c, [...path, "desc"], e.target.value))}
    />
  </div>
);

export const SeoEditor = () => {
  const [lang, setLang] = useState("en");
  const { content, setContent, saveDraft, publish } = useContentDoc(lang);
  const [busy, setBusy] = useState(false);

  const doSaveDraft = async (silent) => {
    setBusy(true);
    try { await saveDraft(content); if (!silent) toast.success("SEO draft saved."); }
    catch { toast.error("Save failed. Are you still signed in?"); }
    finally { setBusy(false); }
  };

  const doPublish = async () => {
    setBusy(true);
    try { await publish(content); toast.success(`SEO published for ${lang.toUpperCase()}.`); }
    catch { toast.error("Publish failed. Are you still signed in?"); }
    finally { setBusy(false); }
  };

  if (!content) return <p className="font-mono text-sm text-[#8A94A2]">Loading...</p>;

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-4 mb-3">
        <div>
          <h2 className="font-serif text-xl text-[#121926]">SEO — Titles & Descriptions</h2>
          <p className="text-xs text-[#718096] mt-1">Per page, per language — including the three dedicated service pages.</p>
        </div>
        <EditorToolbar onSaveDraft={doSaveDraft} onPublish={doPublish} busy={busy} prefix="seo" />
      </div>
      <LangTabs lang={lang} setLang={setLang} />
      <div className="grid md:grid-cols-2 gap-4">
        {PAGES.map((page) => (
          <SeoCard key={`${lang}-${page}`} id={page} path={["seo", page]} node={content.seo[page]} setContent={setContent} />
        ))}
        {content.seo.servicePages && SERVICE_PAGES.map((sp) => (
          <SeoCard key={`${lang}-${sp}`} id={`services/${sp}`} path={["seo", "servicePages", sp]} node={content.seo.servicePages[sp]} setContent={setContent} />
        ))}
      </div>
    </div>
  );
};
